// Deep Link Configuration for AMAAR
// Centralized URL parsing and route mapping for deep linking.
// Used by the web app for smart app banners and social meta tags,
// and can be imported by a React Native app for its linking config.

export const DEEP_LINK_DOMAIN = "amaar.uz";
export const DEEP_LINK_BASE_URL = `https://${DEEP_LINK_DOMAIN}`;

// --- App Identifiers (replace with real values before production) ---
export const IOS_APP_ID = "APPLE_APP_ID_HERE"; // Numeric App Store ID
export const IOS_BUNDLE_ID = "uz.amaar.app";
export const ANDROID_PACKAGE = "uz.amaar.app";

/**
 * All deep-linkable route definitions.
 * `pattern` uses `:param` syntax matching react-router conventions.
 */
export interface DeepLinkRoute {
  /** Human-readable screen name */
  screen: string;
  /** URL pattern with :param placeholders */
  pattern: string;
  /** Parameter names extracted from this route */
  params: string[];
}

export const DEEP_LINK_ROUTES: DeepLinkRoute[] = [
  {
    screen: "ProductDetail",
    pattern: "/:lang/product/:id",
    params: ["lang", "id"],
  },
  {
    screen: "Category",
    pattern: "/:lang/category/:id",
    params: ["lang", "id"],
  },
  {
    screen: "Store",
    pattern: "/:lang/store/:store_address",
    params: ["lang", "store_address"],
  },
  {
    screen: "DeliveryTracking",
    pattern: "/:lang/tracking/:id",
    params: ["lang", "id"],
  },
  {
    screen: "Comments",
    pattern: "/:lang/comments/:id",
    params: ["lang", "id"],
  },
  {
    screen: "Search",
    pattern: "/:lang/search",
    params: ["lang"],
  },
  {
    screen: "Basket",
    pattern: "/:lang/basket",
    params: ["lang"],
  },
  {
    screen: "Favorites",
    pattern: "/:lang/favorites",
    params: ["lang"],
  },
  {
    screen: "Profile",
    pattern: "/:lang/profile",
    params: ["lang"],
  },
  {
    screen: "OrdersConfirmation",
    pattern: "/:lang/orders-confirmation",
    params: ["lang"],
  },
  {
    screen: "OrderSuccess",
    pattern: "/:lang/orders-success",
    params: ["lang"],
  },
  {
    screen: "TopProducts",
    pattern: "/:lang/top-product",
    params: ["lang"],
  },
  {
    screen: "Questions",
    pattern: "/:lang/questions",
    params: ["lang"],
  },
  {
    screen: "Agreement",
    pattern: "/:lang/agreement",
    params: ["lang"],
  },
  {
    screen: "About",
    pattern: "/:lang/about",
    params: ["lang"],
  },
  {
    screen: "Home",
    pattern: "/:lang",
    params: ["lang"],
  },
];

// --- Internal helpers ---

/**
 * Convert a route pattern like "/:lang/product/:id"
 * into a RegExp that captures named groups.
 */
function patternToRegex(pattern: string): RegExp {
  // Escape special regex chars except for our :param placeholders
  const regexStr = pattern
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    .replace(/:(\w+)/g, "(?<$1>[^/]+)");
  return new RegExp(`^${regexStr}$`);
}

// --- Public API ---

export interface ParsedDeepLink {
  screen: string;
  params: Record<string, string>;
  url: string;
}

/**
 * Parse a full URL or pathname into a screen name + params.
 *
 * @example
 * parseDeepLink("https://amaar.uz/uz/product/42")
 * // => { screen: "ProductDetail", params: { lang: "uz", id: "42" }, url: "..." }
 *
 * parseDeepLink("/ru/category/5")
 * // => { screen: "Category", params: { lang: "ru", id: "5" }, url: "..." }
 */
export function parseDeepLink(url: string): ParsedDeepLink | null {
  let pathname: string;

  try {
    // Handle both full URLs and bare pathnames
    if (url.startsWith("http")) {
      const parsed = new URL(url);
      pathname = parsed.pathname;
    } else {
      pathname = url;
    }
  } catch {
    return null;
  }

  // Normalize: remove trailing slash (but keep root "/")
  if (pathname.length > 1 && pathname.endsWith("/")) {
    pathname = pathname.slice(0, -1);
  }

  for (const route of DEEP_LINK_ROUTES) {
    const regex = patternToRegex(route.pattern);
    const match = pathname.match(regex);

    if (match?.groups) {
      return {
        screen: route.screen,
        params: { ...match.groups },
        url,
      };
    }
  }

  return null;
}

/**
 * Build a full deep link URL from a screen name and params.
 *
 * @example
 * buildDeepLink("ProductDetail", { lang: "uz", id: "42" })
 * // => "https://amaar.uz/uz/product/42"
 */
export function buildDeepLink(
  screen: string,
  params: Record<string, string>
): string | null {
  const route = DEEP_LINK_ROUTES.find((r) => r.screen === screen);
  if (!route) return null;

  let path = route.pattern;
  for (const [key, value] of Object.entries(params)) {
    path = path.replace(`:${key}`, encodeURIComponent(value));
  }

  return `${DEEP_LINK_BASE_URL}${path}`;
}

/**
 * Check whether a given pathname maps to an in-app screen.
 */
export function isDeepLinkable(pathname: string): boolean {
  return parseDeepLink(pathname) !== null;
}

/**
 * Generate a React Navigation–compatible linking configuration.
 * This is intended for React Native apps using @react-navigation.
 *
 * @example
 * const linking = getReactNavigationLinkingConfig();
 * // Use with: <NavigationContainer linking={linking}>
 */
export function getReactNavigationLinkingConfig() {
  const screens: Record<string, string> = {};

  for (const route of DEEP_LINK_ROUTES) {
    // Convert "/:lang/product/:id" to ":lang/product/:id" (remove leading slash)
    screens[route.screen] = route.pattern.slice(1);
  }

  return {
    prefixes: [
      DEEP_LINK_BASE_URL,
      `https://www.${DEEP_LINK_DOMAIN}`,
      // Custom URL scheme for direct app launches
      "amaar://",
    ],
    config: {
      screens,
    },
  };
}
